import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode"; // Ensure jwt-decode is installed
import { useNavigate, useParams } from "react-router-dom";
import "./baseStyles.css"; 
import "./ItemManagerPage.css";
import UserListItems from "./UserListItems";


const apiUrl = process.env.REACT_APP_API_URL;

const ItemManagerPage = () => {
  const [campaigns, setCampaigns] = useState([]); // Campaign dropdown
  const [items, setItems] = useState([]); // Items table
  const [form, setForm] = useState({
    campaignItemsId: "",
    campaignItemsType: "",
    campaignItemsAuthor: "",
    campaignItemsName: "",
    campaignItemsText: "",
    campaignItemsURL1: "",
  });
  //const [selectedCampaign, setSelectedCampaign] = useState("");
  const [isQA, setIsQA] = useState(false); // Track if QA campaign
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userId, setUserId] = useState(""); // Dynamically resolved user ID
  const [nextItem, setNextItem] = useState(null); // Store the next item details
  const [cognitoUserId, setCognitoUserId] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const { campaignName } = useParams();
  const [selectedCampaign, setSelectedCampaign] = useState(campaignName || "");
  const [campaignType, setCampaignType] = useState(""); // Track campaign type
  const isReadOnly = campaignType === "Favorites" || campaignType === "CommercialReminders";
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentSeq, setCurrentSeq] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [selectedDisplayName, setSelectedDisplayName] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const [topSeeded, setTopSeeded] = useState(false);


  const isListOfQuestions = (selectedCampaign || "").toLowerCase().endsWith("listofquestions");
  const readNextDisabled = !selectedCampaign || advancing || isListOfQuestions;
  const isSystemTool = campaignType === "SystemTool";


const isUserList = campaignType === "UserList";

const selectedCampaignRow = campaigns.find(
  (c) => c.campaignName === selectedCampaign
);

const userListProcessorType =
  selectedCampaignRow?.myOrganizerProcessorType || "simple_list";

// Existing Organizer-managed UserLists available as move destinations.
// Keep this scoped to Organizer lists so Favorites, Big Question, etc.
// can never appear in the Move dropdown.
const organizerMoveLists = campaigns
  .filter(
    (c) =>
      c.campaignListCampaignType === "UserList" &&
      c.myOrganizerManaged === true
  )
  .map((c) => ({
    campaignName: c.campaignName,
    displayName:
      c.campaignListDisplayName ||
      c.campaignName.replace(`${userId}_myOrganizer_`, ""),
    processorType: c.myOrganizerProcessorType || "simple_list",
  }));



const navigate = useNavigate();

useEffect(() => {
  const idToken = localStorage.getItem("idToken");

  if (!idToken) {
    console.warn("🚨 No idToken found! Redirecting to login...");
    setError("User is not authenticated. Please log in.");
    navigate("/login");
    return;
  }

  try {
    const decodedToken = jwtDecode(idToken);
    console.log("✅ Decoded Token:", decodedToken);

    if (decodedToken.exp * 1000 < Date.now()) {
      console.warn("🚨 Token expired. Redirecting to login...");
      localStorage.removeItem("idToken");
      setError("Session expired. Please log in again.");
      navigate("/login");
      return;
    }

    setUserId(decodedToken.sub);
    fetchAllCampaigns(decodedToken.sub);



    // URL campaign loading is handled after campaigns are fetched,
    // so we can resolve the campaign type before rendering.




  } catch (error) {
    console.error("❌ Error decoding token:", error);
    setError("Authentication error. Please log in again.");
    localStorage.removeItem("idToken");
    navigate("/login");
  }
}, []);

const handleAuthError = (err) => {
  console.error("❌ API Request Failed:", err);

  if (err.response && err.response.status === 401) {
    console.warn("🚨 Unauthorized! Redirecting to login...");
    localStorage.removeItem("idToken");
    setError("Session expired. Please log in again.");
    navigate("/login");
  } else {
    setError("An error occurred. Please try again.");
  }
};


const clearStatusMessages = () => {
  setError("");
  /* setInfoMessage(""); doesnt have one */

};



// reset seeding when campaign changes
useEffect(() => {
  setTopSeeded(false);
  setCurrentItem(null);
}, [selectedCampaign]);

// Resolve URL campaign after campaign list loads
useEffect(() => {
  if (!campaignName || campaigns.length === 0) return;

  const match = campaigns.find((c) =>
    c.campaignName === campaignName ||
    c.campaignListDisplayName === campaignName
  );

  if (!match) return;

  setSelectedCampaign(match.campaignName);
  setCampaignType(match.campaignListCampaignType || determineCampaignType(match.campaignName));
  fetchItems(match.campaignName);
}, [campaignName, campaigns]);





// currentSequence and currentItem
useEffect(() => {
  if (!selectedCampaign) {
    setCurrentItem(null);
    setSelectedDisplayName("");
    setTopSeeded(false);
    return;
  }
  if (items.length === 0 || campaigns.length === 0) return;
  if (topSeeded) return; // <-- don’t overwrite after first seed

  const c = campaigns.find(c => c.campaignName === selectedCampaign);
  const seq = c && c.campaignListNextSeq != null ? Number(c.campaignListNextSeq) : null;

  // previous id (wrap) from the full list
  const ids = items.map(i => Number(i.campaignItemsId)).sort((a,b)=>a-b);
  const idx = seq != null ? ids.indexOf(seq) : -1;
  const prevId = idx >= 0 ? ids[(idx - 1 + ids.length) % ids.length] : ids[0] ?? null;

  const seedId = prevId ?? seq ?? null;
  const found = seedId != null ? items.find(i => Number(i.campaignItemsId) === seedId) : null;

  setCurrentItem(found || null);
  setSelectedDisplayName(
    selectedCampaign.replace(`${userId}_`, "").replace(/^BigQuestion_/, "")
  );
  setTopSeeded(true);
}, [selectedCampaign, items, campaigns, userId, topSeeded]);










// Ensure Authorization Token is included in API requests
const fetchAllCampaigns = async (userid) => {
  setLoading(true);
  clearStatusMessages();

  try {
    const response = await axios.get(
      `${apiUrl}`,
      {
        params: { operation: "fetch_all", userid },
        headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
      }
    );

    if (response.data.message === "No campaigns found") {
      setCampaigns([]);
    } else {
      const data = Array.isArray(response.data) ? response.data : [response.data];
      setCampaigns(data);
    }
  } catch (err) {
    handleAuthError(err);
  } finally {
    setLoading(false);
  }
};


const determineCampaignType = (name) => {
  if (name.includes("_Favorites")) return "Favorites";
  if (name.includes("_PersonalReminders")) return "PersonalReminders";
  if (name.includes("_CommercialReminders")) return "CommercialReminders";
  if (name.includes("_BigQuestion")) return "BigQuestion";
  if (name.includes("_DailyReminders")) return "DailyReminders";
  return "";
};




// 2) In fetchItems: clear first, then set seq + show the previous of that seq
const fetchItems = (campaignName) => {
  const idToken = localStorage.getItem("idToken");
  const { sub: userId } = jwtDecode(idToken);

  setTopSeeded(false);

  setLoading(true);
  clearStatusMessages();
  setItems([]); // ensure UI doesn't show old campaign items

  setCurrentItem(null);

  const payload = { operation: "fetch_campaign_items", userid: userId, campaignName };

  axios
    .post(`${apiUrl}`, payload, { headers: { Authorization: `Bearer ${idToken}` } })
    .then((response) => {
      const data = Array.isArray(response.data) ? response.data : [];
      setItems(data);

      // pointer from campaigns
      const seqFromCampaign = (() => {
        const c = campaigns.find((c) => c.campaignName === campaignName);
        return c && c.campaignListNextSeq != null ? Number(c.campaignListNextSeq) : null;
      })();
      setCurrentSeq(seqFromCampaign);

      // show previous of the pointer so first click advances visibly
      const showId = getPrevId(seqFromCampaign, data);
      setCurrentItem(
        showId != null ? data.find((i) => Number(i.campaignItemsId) === showId) || null : null
      );

      // type
      const campaignRow = campaigns.find((c) => c.campaignName === campaignName);
      if (data.length > 0 && data[0].campaign_type) {
        setCampaignType(data[0].campaign_type);
      } else if (campaignRow?.campaignListCampaignType) {
        setCampaignType(campaignRow.campaignListCampaignType);
      } else {
        setCampaignType(determineCampaignType(campaignName));
      }
    })
    .catch((err) => {
      console.error("❌ Error fetching items:", err);
      setError("Failed to load items.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    })
    .finally(() => setLoading(false));
};











const handleCampaignChange = (e) => {
  const name = e.target.value;
  const campaignRow = campaigns.find((c) => c.campaignName === name);

  // Keep the URL in sync with the selected campaign so a browser refresh
  // reloads the list currently being viewed instead of the old URL list.
  if (name) {
    navigate(`/item-manager/${encodeURIComponent(name)}`);
  }

  setSelectedCampaign(name);
  setIsQA(name.includes("_Favorites"));
  setCampaignType(campaignRow?.campaignListCampaignType || determineCampaignType(name));
  setCurrentItem(null);
  setNextItem(null);   // legacy bottom section
  setItems([]);        // prevent cross-campaign bleed
  setTopSeeded(false);
  fetchItems(name);
};


// Top panel - helper functions
// --- sequence helpers ---
const idsOf = (list) => list.map(i => Number(i.campaignItemsId)).sort((a,b)=>a-b);

const getPrevId = (seq, list) => {
  if (seq == null || !list?.length) return null;
  const ids = idsOf(list);
  const idx = ids.indexOf(Number(seq));
  if (idx === -1) return null;
  const prevIdx = idx > 0 ? idx - 1 : ids.length - 1; // wrap
  return ids[prevIdx];
};

const getNextId = (seq, list) => {
  if (seq == null || !list?.length) return null;
  const ids = idsOf(list);
  const idx = ids.indexOf(Number(seq));
  if (idx === -1) return null;
  const nextIdx = (idx + 1) % ids.length; // wrap
  return ids[nextIdx];
};



// Fetch next item (updates TOP panel only)
const handleReadNext = () => {
  if (!selectedCampaign || advancing || isListOfQuestions) return;
  setAdvancing(true);

  const idToken = localStorage.getItem("idToken");
  const { sub: userId } = jwtDecode(idToken);
  clearStatusMessages();

  axios
    .post(
      `${apiUrl}`,
      { operation: "read_next_item", userid: userId, campaignName: selectedCampaign },
      { headers: { Authorization: `Bearer ${idToken}` } }
    )
    .then((res) => {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!item || item.campaignItemsId == null) return;

      const newId = Number(item.campaignItemsId);

      // keep full list in sync
      setItems((prev) => {
        const exists = prev.some((i) => Number(i.campaignItemsId) === newId);
        return exists
          ? prev.map((i) => (Number(i.campaignItemsId) === newId ? { ...i, ...item } : i))
          : [...prev, item];
      });

      // drive the top panel
      setCurrentItem(item);
      setCurrentSeq(newId);

      // reflect new seq in campaigns cache
      setCampaigns((prev) =>
        prev.map((c) =>
          c.campaignName === selectedCampaign ? { ...c, campaignListNextSeq: newId } : c
        )
      );
    })
    .catch((err) => {
      console.error("Error fetching next item:", err);
      setError("Failed to fetch the next item.");
    })
    .finally(() => setAdvancing(false));
};








  // Handle form updates
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Ensure the form is editable without immediately switching to update mode
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value
    }));
  };




  // Submit form (Add or Update)
const handleSubmit = (isUpdate = false) => {

  clearStatusMessages();

  if (!userId) {
    setError("User ID not found. Please log in.");
    return;
  }


  const token = localStorage.getItem("idToken");
  if (!token) {
    console.error("🚨 No token found. Redirecting to login...");
    setError("User is not authenticated. Please log in.");
    //navigate("/login");
    return;
  }



  const payload = {
    operation: isUpdate ? "update_campaign_item" : "add_campaign_item",
    userid: userId,
    campaignName: selectedCampaign,
    ...(isUpdate && { campaignItemsId: parseInt(form.campaignItemsId, 10) }),
    campaignItemsType: form.campaignItemsType,
    campaignItemsAuthor: form.campaignItemsAuthor,
    campaignItemsName: form.campaignItemsName,
    campaignItemsText: form.campaignItemsText,
    campaignItemsURL1: form.campaignItemsURL1,
  };

  axios
    .post(
      `${apiUrl}`,
      payload,

      {
        headers: {
          Authorization: `Bearer ${token}`, // ✅ Added token here
        },
      }



    )
    .then((response) => {
      //alert(response.data.message);
      fetchItems(selectedCampaign); // ✅ Refresh the list

      // ✅ Reset the form so "Add" mode is available again
      setForm({
        campaignItemsId: "",
        campaignItemsType: "",
        campaignItemsAuthor: "",
        campaignItemsName: "",
        campaignItemsText: "",
        campaignItemsURL1: "",
      });
      setShowEditModal(false); // ✅ Close the modal

    })
    .catch((err) => {
      console.error("Error saving data:", err);
      setError("Failed to save data.");
      window.scrollTo({ top: 0, behavior: "smooth" });

    });
};

const handleDelete = (itemId) => {
  const idToken = localStorage.getItem("idToken");

  if (!idToken) {
    setError("User is not authenticated. Please log in.");
    navigate("/login");
    return;
  }

  const payload = {
    operation: "delete_campaign_item",
    userid: userId,
    campaignName: selectedCampaign,
    campaignItemsId: itemId,
  };

  axios
    .post(
      `${apiUrl}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    )
    .then(() => {
      fetchItems(selectedCampaign); // Refresh items after deletion
    })
    .catch((err) => {
      console.error("Failed to delete item:", err);
      setError("Failed to delete item.");
    });
};



const handleOrganizerItemMove = async (
  itemId,
  destinationCampaignName
) => {
  const idToken = localStorage.getItem("idToken");

  if (!idToken) {
    setError("User is not authenticated. Please log in.");
    navigate("/login");
    return false;
  }

  if (!selectedCampaign || !destinationCampaignName) {
    setError("Choose a destination list.");
    return false;
  }

  if (selectedCampaign === destinationCampaignName) {
    return true;
  }

  try {
    const payload = {
      operation: "move_organizer_item",
      userid: userId,
      campaignName: selectedCampaign,
      campaignItemsId: Number(itemId),
      sourceCampaignListRealCampaignName: selectedCampaign,
      destinationCampaignListRealCampaignName: destinationCampaignName,
    };

    console.log("Moving Organizer item:", payload);

    await axios.post(
      `${apiUrl}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    // Refresh the source list. The moved item should disappear here;
    // it will be loaded from the destination when that list is opened.
    await fetchItems(selectedCampaign);
    return true;

  } catch (err) {
    console.error("Failed to move Organizer item:", err);
    setError(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to move item."
    );
    return false;
  }
};


const handleUserListReorder = async (orderedItemIds) => {
  const idToken = localStorage.getItem("idToken");

  if (!idToken) {
    setError("User is not authenticated. Please log in.");
    navigate("/login");
    return false;
  }

  try {
    const payload = {
      operation: "reorder_campaign_items",
      userid: userId,
      campaignName: selectedCampaign,
      orderedItemIds: orderedItemIds.map((id) => Number(id)),
    };

    console.log("Saving UserList order:", payload);

    await axios.post(
      `${apiUrl}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return true;

  } catch (err) {
    console.error("Failed to save item order:", err);
    setError("Failed to save item order.");
    return false;
  }
};








return (
  <div className="container">




    <h2 className="section-header">Manage Items</h2>

    {error && <div className="error-message">{error}</div>}

    {/* 🔽 Campaign Selection */}
    <div className="dropdown-container">
      <label>Select Campaign:</label>
      <select className="compact-select" value={selectedCampaign} onChange={handleCampaignChange}>
        <option value="">-- Select Campaign --</option>
        {campaigns
          .filter((c) =>
            c.campaignListCampaignType === "PersonalReminders" ||
            c.campaignListCampaignType === "CommercialReminders" ||
            c.campaignListCampaignType === "SystemTool" ||
            c.campaignListCampaignType === "UserList"
          )


          .map((c) => {
            const fullName = c.campaignName.replace(`${userId}_`, "");

            const displayName =
              c.campaignListDisplayName ||
              (fullName.startsWith("BigQuestion_")
                ? fullName.replace("BigQuestion_", "")
                : fullName);

            return (
              <option key={c.campaignName} value={c.campaignName}>
                {displayName}
              </option>

            );
          })}
      </select>
    </div>


{selectedCampaign && isUserList && (


    <UserListItems
      items={items}
      selectedCampaign={selectedCampaign}
      processorType={userListProcessorType}
      listRecord={selectedCampaignRow}
      userId={userId}
      onRefresh={() => fetchItems(selectedCampaign)}
      onDelete={handleDelete}
      onReorder={handleUserListReorder}
      organizerMoveLists={organizerMoveLists}
      onMoveItem={handleOrganizerItemMove}
    />


)}




{/* start new top */}
{/* start new top */}
{/* start new top */}


{/* TOP panel — Desktop */}
{selectedCampaign && !isUserList && !isSystemTool && (
  <div className="items-table-container desktop-only" style={{ marginTop: 12 }}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
      <div style={{fontSize:"0.95rem"}}>
        <strong>Current Item</strong>
      </div>
    </div>

    <table className="items-table">
      <thead><tr><th>ID</th><th>Type</th><th>Author</th><th>Name</th><th>Text</th></tr></thead>
      <tbody>
        {currentItem ? (
          <tr>
            <td>{currentItem.campaignItemsId}</td>
            <td>{currentItem.campaignItemsType || "N/A"}</td>
            <td>{currentItem.campaignItemsAuthor || "N/A"}</td>
            <td>{currentItem.campaignItemsName || "N/A"}</td>
            <td>{currentItem.campaignItemsText || "N/A"}</td>
          </tr>
        ) : (
          <tr><td colSpan={5} style={{textAlign:"center"}}>No item found for current sequence.</td></tr>
        )}
      </tbody>
    </table>

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20, marginTop:20}}>


      <button
        className="save-button"
        onClick={handleReadNext}
        disabled={readNextDisabled}
        title={isListOfQuestions ? "Read Next is disabled for *listOfQuestions campaigns" : undefined}
      >
        Get the next item on the list
      </button>


    </div>



    <h4 className="page-header">Full List</h4>
  </div>
)}


{/* TOP panel — Mobile */}
{selectedCampaign && !isUserList && !isSystemTool && (
  <div className="mobile-only" style={{ marginTop: 12 }}>


        <h4 className="page-header">Current Item</h4>

    <div className="campaign-card">
      {currentItem ? (
        <>
          <div className="card-header">
            <strong className="campaign-name">{currentItem.campaignItemsText}</strong>
          </div>

          {currentItem.campaignItemsType !== "BigQuestion" && currentItem.campaignItemsAuthor && (
            <div className="campaign-author"><div>- {currentItem.campaignItemsAuthor}</div></div>
          )}

          {currentItem.campaignItemsType !== "BigQuestion" && (
            <div className="campaign-description-small">
              <div>{currentItem.campaignItemsName}, {currentItem.campaignItemsId}</div>
            </div>
          )}

          <div className="campaign-footer-small">
            <div className="footer-actions">
              {currentItem.campaignItemsURL1 && (
                <a href={currentItem.campaignItemsURL1} target="_blank" rel="noopener noreferrer" className="action-link more-info-link">
                  More Info
                </a>
              )}


                {!isListOfQuestions && (
                  <button className="action-link" onClick={handleReadNext}>
                    Get the next item on the list
                  </button>
                )}




            </div>
          </div>
        </>
      ) : (
        <>
          <div className="card-header"><strong className="campaign-name">No item found for current sequence</strong></div>
          <div className="campaign-footer-small"><div className="footer-actions">


            {!isListOfQuestions && (
              <button className="action-link" onClick={handleReadNext}>
                Get the next item on the list
              </button>
            )}




          </div></div>
        </>
      )}
    </div>
        <br></br><br></br>
        <h4 className="page-header">Full List</h4>
  </div>
)}




{/* end new top */}
{/* end new top */}
{/* end new top */}

{selectedCampaign && !isUserList && isSystemTool && (
  <h4 className="page-header">Full List</h4>
)}


{/* 📋 Items Table - Desktop */}
<div className="items-table-container desktop-only">
  {!isUserList && items.length > 0 && (
    <>
      {items[0].campaignItemsType === "BigQuestion" ? (
        // ✅ BigQuestion layout
        <table className="items-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Text</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.campaignItemsId}>
                <td>{item.campaignItemsId}</td>
                <td>{item.campaignItemsText}</td>
                <td>
                  {!isReadOnly && (
                    <>
                      <button
                        className="action-link"
                        onClick={() => {
                          setForm({ ...item });
                          setShowEditModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="action-link remove-link"
                        onClick={() => handleDelete(item.campaignItemsId)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // ✅ All other campaign types
        <table className="items-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Author</th>
              <th>Name</th>
              <th>Text</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.campaignItemsId}>
                <td>{item.campaignItemsId}</td>
                <td>{item.campaignItemsType}</td>
                <td>{item.campaignItemsAuthor}</td>
                <td>{item.campaignItemsName}</td>
                <td>{item.campaignItemsText}</td>
                <td>
                  {!isReadOnly && (
                    <>
                      <button
                        className="action-link"
                        onClick={() => {
                          setForm({ ...item });
                          setShowEditModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="action-link remove-link"
                        onClick={() => handleDelete(item.campaignItemsId)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )}
</div>









{/* 📱 Items - Mobile Card View */}
<div className="mobile-only">
  {!isUserList && items.map((item) => {
    const itemIsBigQuestion = item.campaignItemsType === "BigQuestion";
    return (
      <div key={item.campaignItemsId} className="campaign-card">
        <div className="card-header">
          <strong className="campaign-name">{item.campaignItemsText}</strong>
        </div>

        {!itemIsBigQuestion && (
          <div className="campaign-author">
            <div>- {item.campaignItemsAuthor}</div>
          </div>
        )}

        {!itemIsBigQuestion && (
          <div className="campaign-description-small">
            <div>{item.campaignItemsName}, {item.campaignItemsId}</div>
          </div>
        )}

        <div className="campaign-footer-small">
          <div className="footer-actions">
            {item.campaignItemsURL1 && (
              <a
                href={item.campaignItemsURL1}
                target="_blank"
                rel="noopener noreferrer"
                className="action-link more-info-link"
              >
                More Info
              </a>
            )}
            {!isReadOnly && (
              <>
                <button
                  className="action-link"
                  onClick={() => {
                    setForm({ ...item });
                    setShowEditModal(true);
                  }}
                >
                  Edit
                </button>
                <button
                  className="action-link remove-link"
                  onClick={() => handleDelete(item.campaignItemsId)}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  })}
</div>





{/* ➕ Add/Edit Modal */}
{!isUserList && !isReadOnly && showEditModal && form && form.campaignItemsId !== undefined && (
  <div className="modal">
    <div className="modal-content">
      <div className="edit-options-container">
        <h3 className="page-header">
          {form.campaignItemsId ? "Update Item" : "Add Item"}
        </h3>

        {/* ✅ BigQuestion Layout */}
        {form.campaignItemsType === "BigQuestion" ? (
          <>
            <label>
              ID:
              <input
                name="campaignItemsId"
                value={form.campaignItemsId || ""}
                disabled
                placeholder="Auto-generated"
              />
            </label>

            <label>
              Main Text:
              <input
                name="campaignItemsText"
                value={form.campaignItemsText || ""}
                onChange={handleFormChange}
                placeholder="Main content (e.g., reminder, quote)"
              />
            </label>

            {/* ✅ Force BigQuestion type on Add */}
            {!form.campaignItemsId && (
              <input
                type="hidden"
                name="campaignItemsType"
                value="BigQuestion"
              />
            )}
          </>
        ) : (
          <>
            {/* ✅ All Other Types Layout */}
            <label>
              ID:
              <input
                name="campaignItemsId"
                value={form.campaignItemsId || ""}
                disabled
                placeholder="Auto-generated"
              />
            </label>

            <label>
              Author:
              <input
                name="campaignItemsAuthor"
                value={form.campaignItemsAuthor || ""}
                onChange={handleFormChange}
                placeholder="Author"
              />
            </label>

            <label>
              Name:
              <input
                name="campaignItemsName"
                value={form.campaignItemsName || ""}
                onChange={handleFormChange}
                placeholder="Optional name of book, list, etc"
              />
            </label>

            <label>
              Main Text:
              <input
                name="campaignItemsText"
                value={form.campaignItemsText || ""}
                onChange={handleFormChange}
                placeholder="Main content (e.g., reminder, quote)"
              />
            </label>

            <label>
              URL:
              <input
                name="campaignItemsURL1"
                value={form.campaignItemsURL1 || ""}
                onChange={handleFormChange}
                placeholder="Optional link (e.g., article, video)"
              />
            </label>
          </>
        )}

        <div className="button-group">
          <button
            className="save-button"
            onClick={() => handleSubmit(!!form.campaignItemsId)}
          >
            {form.campaignItemsId ? "Update" : "Add"}
          </button>
          <button className="save-button" onClick={() => setForm({})}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}



    {!isUserList && !isReadOnly && (
      <div style={{ marginTop: "16px" }}>

      <button
        className="save-button"
        onClick={() => {
          setForm({
            campaignItemsId: "",
            campaignItemsType: selectedCampaign.includes("BigQuestion") ? "BigQuestion" : "",
            campaignItemsAuthor: "",
            campaignItemsName: "",
            campaignItemsText: "",
            campaignItemsURL1: "",
          });

          setShowEditModal(true);
        }}
      >
        Add Item
      </button>



      </div>
    )}







  </div>
);

};

export default ItemManagerPage;
